// Chapter 4: MPCI Plans of Insurance
// Comprehensive coverage of different insurance plans available

const chapter4 = {
  id: 4,
  title: "MPCI Plans of Insurance",
  sections: [
    {
      title: "Overview of MPCI Plans",
      content: `
        <h2>Overview of MPCI Plans</h2>

        <p>The federal crop insurance program offers multiple plans of insurance to meet diverse producer needs. Each plan has unique features, eligibility requirements, and benefits.</p>

        <div class="key-concept">
          <h3>Key Concept: Categories of Plans</h3>
          <p>MPCI plans generally fall into these categories:</p>
          <ul>
            <li><strong>Yield-Based Plans:</strong> Protect against production losses (APH, YP)</li>
            <li><strong>Revenue-Based Plans:</strong> Protect against revenue losses (RP, RP-HPE)</li>
            <li><strong>Area-Based Plans:</strong> Based on county or area performance (ARPI, AYP)</li>
            <li><strong>Livestock Plans:</strong> Protect livestock operations (LRP, LGM)</li>
            <li><strong>Pasture/Forage Plans:</strong> Based on rainfall indices (PRF)</li>
          </ul>
        </div>

        <h3>Choosing the Right Plan</h3>
        <p>Producers should consider:</p>
        <ul>
          <li>Type of operation (crops, livestock, mixed)</li>
          <li>Risk tolerance and financial situation</li>
          <li>Historical yield variability</li>
          <li>Price volatility of commodities produced</li>
          <li>Premium budget</li>
          <li>Marketing strategies</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the basic categories of plans and their primary purpose. Yield plans protect production, revenue plans protect income, and area plans are based on county performance rather than individual farm data.</p>
        </div>
      `
    },
    {
      title: "Actual Production History (APH) Plan",
      content: `
        <h2>Actual Production History (APH) Plan</h2>

        <p>The APH plan is the foundation for most individual coverage options. It uses the producer's historical yields to establish coverage.</p>

        <div class="definition">
          <h4>How APH Works</h4>
          <p>The APH plan calculates the producer's approved yield based on 4-10 years of production history. This yield becomes the basis for calculating the guarantee.</p>
        </div>

        <h3>Coverage Options</h3>
        <ul>
          <li><strong>Coverage Levels:</strong> 50% to 85% (in 5% increments)</li>
          <li><strong>Price Elections:</strong> Up to 100% of the RMA-announced price</li>
          <li><strong>Unit Options:</strong> Basic, Optional, or Enterprise units</li>
        </ul>

        <h3>Guarantee Calculation</h3>
        <div class="key-concept">
          <h3>Key Concept: APH Guarantee Formula</h3>
          <p><strong>Production Guarantee = APH Yield × Coverage Level × Acres</strong></p>
          <p><strong>Dollar Guarantee = Production Guarantee × Price Election</strong></p>
        </div>

        <div class="example">
          <h4>Example: APH Guarantee</h4>
          <p>A corn producer has:</p>
          <ul>
            <li>APH Yield: 160 bu/acre</li>
            <li>Coverage Level: 75%</li>
            <li>Price Election: $4.50/bu</li>
            <li>Acres: 500</li>
          </ul>
          <p><strong>Production Guarantee = 160 × 0.75 × 500 = 60,000 bushels</strong></p>
          <p><strong>Dollar Guarantee = 60,000 × $4.50 = $270,000</strong></p>
        </div>

        <h3>Indemnity Calculation</h3>
        <p>An indemnity is paid when actual production falls below the guarantee:</p>
        <p><strong>Indemnity = (Guarantee - Actual Production) × Price Election</strong></p>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>APH is a yield protection plan - it pays when production falls short. Know the guarantee formula and how indemnities are calculated.</p>
        </div>
      `
    },
    {
      title: "Yield Protection (YP)",
      content: `
        <h2>Yield Protection (YP)</h2>

        <p>Yield Protection provides coverage against yield losses. The price component is fixed at the projected price determined before planting.</p>

        <div class="definition">
          <h4>What is Yield Protection?</h4>
          <p>YP insures the producer's yield at a projected price. If production falls below the guarantee, an indemnity is paid based on the bushels short times the projected price.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Fixed Price:</strong> Uses projected price established before planting season</li>
          <li><strong>Yield Focus:</strong> Only covers production shortfalls</li>
          <li><strong>No Price Protection:</strong> Does NOT protect against price declines</li>
          <li><strong>Lower Premium:</strong> Generally costs less than Revenue Protection</li>
        </ul>

        <h3>Price Determination</h3>
        <div class="key-concept">
          <h3>Key Concept: Projected Price</h3>
          <p>The projected price is based on commodity futures prices during a discovery period before planting:</p>
          <ul>
            <li>Corn/Soybeans: Average of December corn or November soybean futures in February</li>
            <li>Wheat: Various discovery periods depending on type</li>
          </ul>
          <p>This price is used for both guarantee calculation and indemnity payment.</p>
        </div>

        <div class="example">
          <h4>Example: YP Indemnity</h4>
          <p>A producer has:</p>
          <ul>
            <li>Yield Guarantee: 120 bu/acre (160 APH × 75%)</li>
            <li>Projected Price: $4.00/bu</li>
            <li>Actual Yield: 100 bu/acre</li>
            <li>Acres: 500</li>
          </ul>
          <p><strong>Production Loss = (120 - 100) × 500 = 10,000 bushels</strong></p>
          <p><strong>Indemnity = 10,000 × $4.00 = $40,000</strong></p>
        </div>

        <h3>When to Choose YP</h3>
        <ul>
          <li>Producer expects stable or rising prices</li>
          <li>Producer uses other price risk management tools (hedging)</li>
          <li>Premium budget is limited</li>
          <li>Primary concern is protecting yield, not revenue</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>YP uses a FIXED projected price - it does NOT adjust for harvest price changes. This is the key difference from Revenue Protection.</p>
        </div>
      `
    },
    {
      title: "Revenue Protection (RP)",
      content: `
        <h2>Revenue Protection (RP)</h2>

        <p>Revenue Protection is the most popular crop insurance plan. It protects against loss of revenue due to price decline, yield loss, or a combination of both.</p>

        <div class="definition">
          <h4>What is Revenue Protection?</h4>
          <p>RP guarantees a minimum revenue level. The guarantee uses the HIGHER of the projected price or the harvest price, providing upside protection if prices rise.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Revenue Focus:</strong> Protects total revenue, not just yield</li>
          <li><strong>Price Protection:</strong> Covers price declines</li>
          <li><strong>Harvest Price Option:</strong> Guarantee increases if harvest price exceeds projected price</li>
          <li><strong>Higher Premium:</strong> Costs more than YP due to price protection</li>
        </ul>

        <h3>Price Determination</h3>
        <div class="key-concept">
          <h3>Key Concept: Two Prices in RP</h3>
          <ul>
            <li><strong>Projected Price:</strong> Established before planting (same as YP)</li>
            <li><strong>Harvest Price:</strong> Established during harvest period based on futures</li>
          </ul>
          <p>The guarantee uses whichever price is HIGHER, providing protection if prices rise.</p>
        </div>

        <h3>Revenue Guarantee Calculation</h3>
        <p><strong>Revenue Guarantee = APH Yield × Coverage Level × HIGHER(Projected Price, Harvest Price)</strong></p>

        <div class="example">
          <h4>Example: RP with Rising Prices</h4>
          <p>Scenario: Prices INCREASE</p>
          <ul>
            <li>APH Yield: 160 bu/acre</li>
            <li>Coverage Level: 75%</li>
            <li>Projected Price: $4.00/bu</li>
            <li>Harvest Price: $5.00/bu (higher)</li>
            <li>Actual Yield: 140 bu/acre</li>
          </ul>
          <p><strong>Revenue Guarantee = 160 × 0.75 × $5.00 = $600/acre</strong></p>
          <p><strong>Actual Revenue = 140 × $5.00 = $700/acre</strong></p>
          <p><strong>Result: No payment - actual revenue exceeds guarantee</strong></p>
        </div>

        <div class="example">
          <h4>Example: RP with Falling Prices</h4>
          <p>Scenario: Prices DECREASE</p>
          <ul>
            <li>APH Yield: 160 bu/acre</li>
            <li>Coverage Level: 75%</li>
            <li>Projected Price: $4.00/bu (higher)</li>
            <li>Harvest Price: $3.00/bu</li>
            <li>Actual Yield: 160 bu/acre (normal yield)</li>
          </ul>
          <p><strong>Revenue Guarantee = 160 × 0.75 × $4.00 = $480/acre</strong></p>
          <p><strong>Actual Revenue = 160 × $3.00 = $480/acre</strong></p>
          <p><strong>Result: Breakeven - guarantee equals actual</strong></p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>The CRITICAL feature of RP: The guarantee can INCREASE if harvest price exceeds projected price. This is why RP is more expensive than YP or RP-HPE.</p>
        </div>
      `
    },
    {
      title: "Revenue Protection with Harvest Price Exclusion (RP-HPE)",
      content: `
        <h2>Revenue Protection with Harvest Price Exclusion (RP-HPE)</h2>

        <p>RP-HPE is similar to standard Revenue Protection but excludes the harvest price increase feature, resulting in lower premiums.</p>

        <div class="definition">
          <h4>What is RP-HPE?</h4>
          <p>RP-HPE provides revenue protection using only the projected price. The guarantee does NOT increase if harvest price rises above projected price.</p>
        </div>

        <h3>Comparison: RP vs. RP-HPE</h3>
        <table>
          <tr>
            <th>Feature</th>
            <th>RP</th>
            <th>RP-HPE</th>
          </tr>
          <tr>
            <td>Price for Guarantee</td>
            <td>Higher of projected or harvest</td>
            <td>Projected price only</td>
          </tr>
          <tr>
            <td>Guarantee Can Increase</td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Premium</td>
            <td>Higher</td>
            <td>Lower</td>
          </tr>
          <tr>
            <td>Price Protection</td>
            <td>Full (upside and downside)</td>
            <td>Partial (downside only)</td>
          </tr>
        </table>

        <div class="example">
          <h4>Example: RP vs RP-HPE When Prices Rise</h4>
          <ul>
            <li>APH: 160 bu/acre, Coverage: 75%</li>
            <li>Projected Price: $4.00</li>
            <li>Harvest Price: $5.00</li>
            <li>Actual Yield: 100 bu/acre</li>
          </ul>

          <p><strong>With RP:</strong></p>
          <ul>
            <li>Guarantee = 160 × 0.75 × $5.00 = $600/acre</li>
            <li>Actual Revenue = 100 × $5.00 = $500/acre</li>
            <li>Indemnity = $600 - $500 = $100/acre</li>
          </ul>

          <p><strong>With RP-HPE:</strong></p>
          <ul>
            <li>Guarantee = 160 × 0.75 × $4.00 = $480/acre</li>
            <li>Actual Revenue = 100 × $5.00 = $500/acre</li>
            <li>Indemnity = $0 (actual exceeds guarantee)</li>
          </ul>
        </div>

        <h3>When to Choose RP-HPE</h3>
        <ul>
          <li>Producer expects prices to remain stable or fall</li>
          <li>Producer wants revenue protection at lower cost</li>
          <li>Producer pre-sells or hedges production at projected prices</li>
          <li>Premium savings are important</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>RP-HPE excludes the harvest price for calculating the guarantee. This means NO upside price protection, but lower premiums. Know the trade-off.</p>
        </div>
      `
    },
    {
      title: "Area Risk Protection Insurance (ARPI)",
      content: `
        <h2>Area Risk Protection Insurance (ARPI)</h2>

        <p>ARPI is an area-based plan where coverage is based on county yields and revenues rather than individual farm performance.</p>

        <div class="definition">
          <h4>What is ARPI?</h4>
          <p>ARPI pays indemnities based on how the county performs relative to expected levels. Individual farm performance does NOT affect payments.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>County-Based:</strong> Uses county yield/revenue data, not farm data</li>
          <li><strong>No APH Required:</strong> No production records needed</li>
          <li><strong>Subsidized Premiums:</strong> Government premium subsidies available</li>
          <li><strong>Potential Basis Risk:</strong> County performance may differ from farm</li>
        </ul>

        <h3>ARPI Options</h3>
        <div class="key-concept">
          <h3>Key Concept: ARPI Coverage Types</h3>
          <ul>
            <li><strong>Area Yield Protection (AYP):</strong> Based on county yield only</li>
            <li><strong>Area Revenue Protection (ARP):</strong> Based on county revenue with harvest price option</li>
            <li><strong>Area Revenue Protection-HPE:</strong> Based on county revenue without harvest price</li>
          </ul>
        </div>

        <h3>Coverage Levels</h3>
        <p>ARPI offers coverage levels from 70% to 90% of expected county yield or revenue.</p>

        <h3>How Payments Work</h3>
        <div class="example">
          <h4>Example: ARPI Payment</h4>
          <p>County Corn AYP:</p>
          <ul>
            <li>Expected County Yield: 150 bu/acre</li>
            <li>Coverage Level: 85%</li>
            <li>Trigger Yield: 150 × 0.85 = 127.5 bu/acre</li>
            <li>Actual County Yield: 120 bu/acre</li>
            <li>Price: $4.00/bu</li>
          </ul>
          <p><strong>Payment = (127.5 - 120) × $4.00 = $30/acre</strong></p>
          <p>Note: Payment is the same for all insured producers in the county regardless of individual yields.</p>
        </div>

        <h3>Basis Risk</h3>
        <div class="important">
          <h3>Important: Understanding Basis Risk</h3>
          <p>Because ARPI pays based on county, not individual performance:</p>
          <ul>
            <li>Your farm may have a loss but the county may not - NO payment</li>
            <li>Your farm may be fine but the county has a loss - You receive payment</li>
          </ul>
          <p>This is "basis risk" - the mismatch between your farm and the county.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>ARPI is county-based, not individual. Payments depend on county performance, creating potential basis risk. Know that no APH is required for ARPI.</p>
        </div>
      `
    },
    {
      title: "Livestock Risk Protection (LRP)",
      content: `
        <h2>Livestock Risk Protection (LRP)</h2>

        <p>LRP provides price protection for livestock producers, covering the difference between expected and actual market prices.</p>

        <div class="definition">
          <h4>What is LRP?</h4>
          <p>LRP insures against declining livestock prices. It functions like a put option - if prices fall below the coverage price, the producer receives an indemnity.</p>
        </div>

        <h3>Covered Livestock</h3>
        <ul>
          <li>Fed Cattle</li>
          <li>Feeder Cattle</li>
          <li>Swine</li>
          <li>Lamb</li>
        </ul>

        <h3>How LRP Works</h3>
        <div class="key-concept">
          <h3>Key Concept: LRP Coverage</h3>
          <ol>
            <li>Producer selects coverage price (percentage of expected price)</li>
            <li>Producer selects end date (when coverage ends)</li>
            <li>Producer selects number of head to cover</li>
            <li>At end date, if actual price is below coverage price, indemnity is paid</li>
          </ol>
        </div>

        <h3>Coverage Options</h3>
        <ul>
          <li><strong>Coverage Prices:</strong> Various levels from 70% to 100% of expected price</li>
          <li><strong>Coverage Periods:</strong> 13 to 52 weeks</li>
          <li><strong>Endorsement Periods:</strong> Offered daily with updated prices</li>
        </ul>

        <div class="example">
          <h4>Example: LRP for Fed Cattle</h4>
          <ul>
            <li>Expected End Price: $150/cwt</li>
            <li>Coverage Price Selected: 95% = $142.50/cwt</li>
            <li>Head Covered: 100</li>
            <li>Weight: 1,200 lbs (12 cwt/head)</li>
            <li>Actual Price at End Date: $135/cwt</li>
          </ul>
          <p><strong>Indemnity per cwt = $142.50 - $135.00 = $7.50</strong></p>
          <p><strong>Total Indemnity = 100 head × 12 cwt × $7.50 = $9,000</strong></p>
        </div>

        <h3>Key Differences from Crop Insurance</h3>
        <ul>
          <li>Price-based, not yield-based</li>
          <li>No production history required</li>
          <li>Purchased on a per-head basis</li>
          <li>Available year-round with daily endorsement periods</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>LRP is PRICE protection for livestock, similar to a put option. It pays when market prices fall below the selected coverage price.</p>
        </div>
      `
    },
    {
      title: "Livestock Gross Margin (LGM)",
      content: `
        <h2>Livestock Gross Margin (LGM)</h2>

        <p>LGM provides protection against the loss of gross margin (market value minus feed costs) for livestock operations.</p>

        <div class="definition">
          <h4>What is LGM?</h4>
          <p>LGM insures the difference between the livestock market value and feed costs. It protects the producer's margin, not just the price.</p>
        </div>

        <h3>Available LGM Programs</h3>
        <ul>
          <li><strong>LGM-Cattle:</strong> For cattle feeders</li>
          <li><strong>LGM-Swine:</strong> For hog operations</li>
          <li><strong>LGM-Dairy:</strong> For dairy operations (milk minus feed)</li>
        </ul>

        <h3>How LGM Works</h3>
        <div class="key-concept">
          <h3>Key Concept: Gross Margin Protection</h3>
          <p><strong>Gross Margin = Livestock Value - Feed Costs</strong></p>
          <p>LGM pays when the actual gross margin falls below the expected gross margin covered.</p>
        </div>

        <div class="example">
          <h4>Example: LGM-Cattle</h4>
          <ul>
            <li>Expected Cattle Value: $1,800/head</li>
            <li>Expected Feed Costs: $1,200/head</li>
            <li>Expected Gross Margin: $600/head</li>
            <li>Coverage Level: 90%</li>
            <li>Gross Margin Guarantee: $540/head</li>
          </ul>
          <p>If actual gross margin = $400/head:</p>
          <p><strong>Indemnity = $540 - $400 = $140/head</strong></p>
        </div>

        <h3>LGM vs. LRP</h3>
        <table>
          <tr>
            <th>Feature</th>
            <th>LRP</th>
            <th>LGM</th>
          </tr>
          <tr>
            <td>Protects</td>
            <td>Price only</td>
            <td>Margin (price minus feed)</td>
          </tr>
          <tr>
            <td>Feed Cost Risk</td>
            <td>Not covered</td>
            <td>Covered</td>
          </tr>
          <tr>
            <td>Availability</td>
            <td>Daily</td>
            <td>Monthly sales periods</td>
          </tr>
          <tr>
            <td>Premium Payment</td>
            <td>At purchase</td>
            <td>At end of coverage</td>
          </tr>
        </table>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>LGM protects MARGIN (price minus feed costs), while LRP only protects price. LGM is more comprehensive but more complex.</p>
        </div>
      `
    },
    {
      title: "Pasture, Rangeland, Forage (PRF)",
      content: `
        <h2>Pasture, Rangeland, Forage (PRF)</h2>

        <p>PRF is a rainfall index insurance program that provides protection for pasture, rangeland, and forage based on precipitation levels.</p>

        <div class="definition">
          <h4>What is PRF?</h4>
          <p>PRF pays indemnities when precipitation in a specific area falls below normal levels during selected time periods. It is NOT based on actual pasture or forage production.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Index-Based:</strong> Uses rainfall data, not production data</li>
          <li><strong>Grid-Based:</strong> Coverage based on 12×12 mile grids</li>
          <li><strong>No Inspection:</strong> No loss adjustment - payments automatic based on data</li>
          <li><strong>Interval Selection:</strong> Producer chooses 2-month intervals to cover</li>
        </ul>

        <h3>How PRF Works</h3>
        <div class="key-concept">
          <h3>Key Concept: Rainfall Index</h3>
          <ol>
            <li>Producer selects grid(s) where land is located</li>
            <li>Producer selects 2-month intervals (index intervals)</li>
            <li>Producer selects productivity factor and coverage level</li>
            <li>If rainfall during interval is below trigger, payment is made</li>
          </ol>
        </div>

        <h3>Index Intervals</h3>
        <p>PRF uses 2-month intervals throughout the year:</p>
        <ul>
          <li>January-February, February-March, March-April, etc.</li>
          <li>Producer must select at least 2 intervals</li>
          <li>No single interval can have more than 70% of coverage value</li>
        </ul>

        <div class="example">
          <h4>Example: PRF Payment</h4>
          <ul>
            <li>Selected Interval: April-May</li>
            <li>Expected Rainfall Index: 100% (normal)</li>
            <li>Coverage Level: 85%</li>
            <li>Trigger: 85% of normal</li>
            <li>Actual Rainfall Index: 70% of normal</li>
            <li>Dollar Amount of Coverage: $10,000</li>
          </ul>
          <p><strong>Payment Factor = (85% - 70%) ÷ 85% = 17.6%</strong></p>
          <p><strong>Indemnity = $10,000 × 17.6% = $1,760</strong></p>
        </div>

        <h3>Basis Risk in PRF</h3>
        <div class="important">
          <h3>Important: PRF Basis Risk</h3>
          <p>Because PRF uses grid-based rainfall data:</p>
          <ul>
            <li>Your specific location may receive different rainfall than the grid average</li>
            <li>You may have drought conditions but receive no payment (grid has adequate rain)</li>
            <li>You may receive payment even without drought (grid has low rainfall)</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>PRF is rainfall index insurance for pasture/forage. It is NOT based on actual production - payments depend on grid rainfall compared to historical normal.</p>
        </div>
      `
    },
    {
      title: "Whole-Farm Revenue Protection (WFRP)",
      content: `
        <h2>Whole-Farm Revenue Protection (WFRP)</h2>

        <p>WFRP provides a risk management safety net for all commodities on the farm under one policy. It is designed for diversified operations.</p>

        <div class="definition">
          <h4>What is WFRP?</h4>
          <p>WFRP insures the entire farm's revenue from all commodities produced. It uses the farm's Schedule F tax records to establish expected revenue.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Whole-Farm Approach:</strong> One policy covers all commodities</li>
          <li><strong>Tax-Based:</strong> Uses Schedule F to determine revenue</li>
          <li><strong>Diversification Benefit:</strong> Higher coverage for diversified operations</li>
          <li><strong>Specialty Crops:</strong> Good option for farms with unique crops</li>
        </ul>

        <h3>Eligibility</h3>
        <ul>
          <li>Farms with 5 or more years of tax records</li>
          <li>Expected revenue up to $17 million (as of recent rules)</li>
          <li>Must grow at least one commodity (crops, livestock, or specialty)</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: WFRP Coverage</h3>
          <p><strong>Revenue Guarantee = Expected Revenue × Coverage Level</strong></p>
          <p>Expected revenue is based on 5-year average of Schedule F revenue, indexed for commodity prices.</p>
        </div>

        <h3>Coverage Levels</h3>
        <ul>
          <li>50% to 85% for operations with commodity count less than 3</li>
          <li>Up to 85% with additional coverage for highly diversified farms</li>
        </ul>

        <div class="example">
          <h4>Example: WFRP</h4>
          <ul>
            <li>Expected Farm Revenue: $500,000</li>
            <li>Coverage Level: 75%</li>
            <li>Revenue Guarantee: $375,000</li>
            <li>Actual Farm Revenue: $300,000</li>
          </ul>
          <p><strong>Indemnity = $375,000 - $300,000 = $75,000</strong></p>
        </div>

        <h3>When to Consider WFRP</h3>
        <ul>
          <li>Diversified operations growing multiple commodities</li>
          <li>Farms with specialty crops not covered by other programs</li>
          <li>Operations wanting simplified coverage</li>
          <li>Farms with good tax records</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>WFRP covers the whole farm's revenue based on tax records. It rewards diversification and is good for specialty crop operations.</p>
        </div>
      `
    },
    {
      title: "Margin Protection (MP)",
      content: `
        <h2>Margin Protection (MP)</h2>

        <p>Margin Protection is an area-based policy that protects the margin between crop revenue and input costs.</p>

        <div class="definition">
          <h4>What is Margin Protection?</h4>
          <p>MP provides coverage when the county margin (expected revenue minus input costs) falls below the guarantee level.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Area-Based:</strong> Uses county data, not individual farm</li>
          <li><strong>Cost Component:</strong> Considers input costs (fertilizer, fuel, interest)</li>
          <li><strong>Margin Focus:</strong> Protects profitability, not just revenue</li>
        </ul>

        <h3>Available Crops</h3>
        <p>MP is available for major crops including:</p>
        <ul>
          <li>Corn</li>
          <li>Rice</li>
          <li>Soybeans</li>
          <li>Wheat</li>
        </ul>

        <h3>How MP Works</h3>
        <div class="key-concept">
          <h3>Key Concept: Margin Calculation</h3>
          <p><strong>Expected Margin = Expected Revenue - Expected Costs</strong></p>
          <p><strong>Margin Guarantee = Expected Margin × Coverage Level</strong></p>
          <p>If actual margin falls below guarantee, an indemnity is paid.</p>
        </div>

        <div class="example">
          <h4>Example: Margin Protection</h4>
          <ul>
            <li>Expected County Revenue: $500/acre</li>
            <li>Expected Input Costs: $350/acre</li>
            <li>Expected Margin: $150/acre</li>
            <li>Coverage Level: 80%</li>
            <li>Margin Guarantee: $120/acre</li>
          </ul>
          <p>If actual margin = $90/acre:</p>
          <p><strong>Indemnity = $120 - $90 = $30/acre</strong></p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>MP protects the margin (revenue minus costs), not just revenue. It's area-based and considers input costs like fertilizer and fuel.</p>
        </div>
      `
    },
    {
      title: "Stacked Income Protection Plan (STAX)",
      content: `
        <h2>Stacked Income Protection Plan (STAX)</h2>

        <p>STAX is an area-based revenue protection plan available only for cotton that provides an additional layer of coverage.</p>

        <div class="definition">
          <h4>What is STAX?</h4>
          <p>STAX provides area-based revenue protection for cotton. It can be purchased alone or "stacked" on top of an individual policy to provide additional coverage.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Cotton Only:</strong> Available only for upland cotton</li>
          <li><strong>Area-Based:</strong> Uses county revenue data</li>
          <li><strong>Stackable:</strong> Can be added to individual coverage</li>
          <li><strong>High Subsidy:</strong> Up to 80% premium subsidy</li>
        </ul>

        <h3>Coverage Options</h3>
        <div class="key-concept">
          <h3>Key Concept: STAX Coverage Bands</h3>
          <p>STAX covers a "band" of losses between:</p>
          <ul>
            <li>Upper coverage level: 90% of expected revenue</li>
            <li>Lower coverage level: Selected by producer (minimum 70%)</li>
          </ul>
          <p>STAX pays when county revenue falls within this band.</p>
        </div>

        <div class="example">
          <h4>Example: STAX Coverage</h4>
          <ul>
            <li>Upper Coverage: 90%</li>
            <li>Lower Coverage: 75%</li>
            <li>Expected County Revenue: $400/acre</li>
            <li>STAX Coverage Band: $360/acre (90%) to $300/acre (75%)</li>
          </ul>
          <p>STAX pays when county revenue falls between $300 and $360/acre.</p>
        </div>

        <h3>Stacking with Individual Coverage</h3>
        <p>A producer might have:</p>
        <ul>
          <li>Individual RP at 75% coverage (protects below 75%)</li>
          <li>STAX from 75% to 90% (fills the gap above individual coverage)</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>STAX is for cotton only. It's area-based and can be stacked on individual coverage to provide extra protection in the 75-90% range.</p>
        </div>
      `
    },
    {
      title: "Enhanced Coverage Option (ECO)",
      content: `
        <h2>Enhanced Coverage Option (ECO)</h2>

        <p>ECO is an area-based add-on that provides additional coverage above individual policy levels, designed to cover shallow losses.</p>

        <div class="definition">
          <h4>What is ECO?</h4>
          <p>ECO provides area-based coverage that pays when county revenue or yield falls in the "shallow loss" range - between 86% and the producer's individual coverage level (up to 95%).</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Add-On Coverage:</strong> Purchased in addition to individual policy</li>
          <li><strong>Shallow Losses:</strong> Covers losses above individual deductible</li>
          <li><strong>Area-Based:</strong> Uses county data for triggering payments</li>
          <li><strong>Coverage Range:</strong> 86% to 90% or 86% to 95%</li>
        </ul>

        <h3>ECO Coverage Options</h3>
        <div class="key-concept">
          <h3>Key Concept: ECO Coverage Bands</h3>
          <ul>
            <li><strong>ECO 90:</strong> Covers losses from 90% to 86% of expected county revenue</li>
            <li><strong>ECO 95:</strong> Covers losses from 95% to 86% of expected county revenue</li>
          </ul>
          <p>The lower trigger is always 86% of expected county revenue.</p>
        </div>

        <div class="example">
          <h4>Example: ECO with Individual Coverage</h4>
          <p>A producer has:</p>
          <ul>
            <li>Individual RP at 75% coverage</li>
            <li>ECO 90: Covers 90% to 86% range</li>
          </ul>
          <p>Coverage structure:</p>
          <ul>
            <li>ECO 90 pays: County falls in 86-90% range</li>
            <li>GAP (86% to 75%): Uninsured</li>
            <li>Individual RP pays: Individual falls below 75%</li>
          </ul>
        </div>

        <h3>How Payments Work</h3>
        <p>ECO payments are based on county performance:</p>
        <ul>
          <li>If county revenue is above 90% (or 95%), no ECO payment</li>
          <li>If county revenue falls between trigger levels, partial payment</li>
          <li>If county revenue is below 86%, maximum ECO payment</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>ECO is for "shallow losses" - small county-wide revenue drops. It's an add-on, area-based coverage that complements individual policies.</p>
        </div>
      `
    },
    {
      title: "Supplemental Coverage Option (SCO)",
      content: `
        <h2>Supplemental Coverage Option (SCO)</h2>

        <p>SCO is an area-based coverage that fills the gap between the producer's individual coverage level and 86% coverage.</p>

        <div class="definition">
          <h4>What is SCO?</h4>
          <p>SCO provides county-based protection that covers losses from 86% down to the producer's underlying policy coverage level. It is tied to the individual policy.</p>
        </div>

        <h3>Key Features</h3>
        <ul>
          <li><strong>Gap Filler:</strong> Covers between 86% and individual coverage level</li>
          <li><strong>Area-Based:</strong> Uses county data for payment triggers</li>
          <li><strong>Tied to Individual:</strong> Must have underlying individual policy</li>
          <li><strong>Same Crop:</strong> Must match the underlying policy's crop</li>
        </ul>

        <h3>How SCO Works</h3>
        <div class="key-concept">
          <h3>Key Concept: SCO Coverage Band</h3>
          <p>SCO covers from 86% of expected county revenue/yield down to the producer's individual coverage level.</p>
          <ul>
            <li>Upper trigger: 86% of expected county revenue</li>
            <li>Lower trigger: Producer's individual coverage level</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: SCO Coverage</h4>
          <p>Producer has individual RP at 75%:</p>
          <ul>
            <li>SCO covers: 86% to 75% = 11 percentage point band</li>
            <li>Expected County Revenue: $500/acre</li>
            <li>SCO Upper Trigger: $430/acre (86%)</li>
            <li>SCO Lower Trigger: $375/acre (75%)</li>
          </ul>
          <p>If county revenue = $400/acre (80%):</p>
          <ul>
            <li>SCO payment triggered: County is below 86%</li>
            <li>SCO Loss = ($430 - $400) ÷ ($430 - $375) × coverage</li>
          </ul>
        </div>

        <h3>SCO vs. ECO</h3>
        <table>
          <tr>
            <th>Feature</th>
            <th>SCO</th>
            <th>ECO</th>
          </tr>
          <tr>
            <td>Coverage Range</td>
            <td>86% to individual level</td>
            <td>90% or 95% to 86%</td>
          </tr>
          <tr>
            <td>Purpose</td>
            <td>Fill gap in deductible</td>
            <td>Cover shallow losses above 86%</td>
          </tr>
          <tr>
            <td>Requires Individual</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
        </table>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>SCO fills the gap from 86% to your individual coverage level. ECO covers ABOVE 86% (to 90% or 95%). Both are area-based add-ons.</p>
        </div>
      `
    },
    {
      title: "Comparing Insurance Plans",
      content: `
        <h2>Comparing Insurance Plans</h2>

        <p>Choosing the right plan requires understanding the trade-offs between coverage types. This comparison helps agents advise producers.</p>

        <h3>Individual vs. Area-Based Plans</h3>
        <table>
          <tr>
            <th>Feature</th>
            <th>Individual Plans (APH, RP)</th>
            <th>Area Plans (ARPI, SCO)</th>
          </tr>
          <tr>
            <td>Basis</td>
            <td>Your farm's data</td>
            <td>County data</td>
          </tr>
          <tr>
            <td>Production Records</td>
            <td>Required (APH)</td>
            <td>Not required</td>
          </tr>
          <tr>
            <td>Basis Risk</td>
            <td>Low (based on your farm)</td>
            <td>Higher (county may differ)</td>
          </tr>
          <tr>
            <td>Premium</td>
            <td>Based on your history</td>
            <td>Often lower</td>
          </tr>
        </table>

        <h3>Yield vs. Revenue Plans</h3>
        <table>
          <tr>
            <th>Feature</th>
            <th>Yield Plans (YP)</th>
            <th>Revenue Plans (RP)</th>
          </tr>
          <tr>
            <td>Protects</td>
            <td>Production only</td>
            <td>Production AND price</td>
          </tr>
          <tr>
            <td>Premium</td>
            <td>Lower</td>
            <td>Higher</td>
          </tr>
          <tr>
            <td>Price Protection</td>
            <td>None</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Best When</td>
            <td>Hedging/stable prices</td>
            <td>Price volatility expected</td>
          </tr>
        </table>

        <div class="key-concept">
          <h3>Key Concept: Building a Coverage Package</h3>
          <p>Producers often combine coverages:</p>
          <ul>
            <li><strong>Base:</strong> Individual policy (RP at 75%)</li>
            <li><strong>Add SCO:</strong> Fills gap from 75% to 86%</li>
            <li><strong>Add ECO:</strong> Covers 86% to 90%/95%</li>
            <li><strong>Add Crop Hail:</strong> Named peril protection</li>
          </ul>
        </div>

        <h3>Questions to Ask Producers</h3>
        <ul>
          <li>What is your primary concern - yield or revenue?</li>
          <li>Do you hedge or forward contract?</li>
          <li>How diversified is your operation?</li>
          <li>What is your premium budget?</li>
          <li>How does your farm compare to the county average?</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Understand the fundamental differences: Individual vs. area-based, yield vs. revenue. Know when each type is most appropriate.</p>
        </div>
      `
    }
  ],
  quizQuestions: [
    {
      question: "The APH plan establishes coverage based on:",
      options: [
        "County average yields",
        "The producer's historical production records",
        "National commodity prices",
        "Neighboring farm performance"
      ],
      correct: 1,
      explanation: "APH (Actual Production History) uses the producer's own 4-10 years of historical yields to calculate the approved yield and coverage guarantee."
    },
    {
      question: "Yield Protection (YP) uses which price to calculate guarantees and indemnities?",
      options: [
        "Harvest price only",
        "Higher of projected or harvest price",
        "Projected price only",
        "Average of projected and harvest prices"
      ],
      correct: 2,
      explanation: "YP uses only the projected price established before planting. It does NOT adjust for harvest price changes - that's what distinguishes it from Revenue Protection."
    },
    {
      question: "The key feature that distinguishes RP from RP-HPE is:",
      options: [
        "RP covers yield losses while RP-HPE covers revenue losses",
        "RP's guarantee can increase if harvest price exceeds projected price",
        "RP-HPE has higher premiums",
        "RP is area-based while RP-HPE is individual"
      ],
      correct: 1,
      explanation: "With standard RP, the revenue guarantee uses the HIGHER of projected or harvest price, so the guarantee can increase if prices rise. RP-HPE excludes this feature."
    },
    {
      question: "Area Risk Protection Insurance (ARPI) payments are based on:",
      options: [
        "Individual farm performance only",
        "County performance, not individual farm data",
        "National commodity prices",
        "Weather station data"
      ],
      correct: 1,
      explanation: "ARPI is an area-based plan. Payments are triggered by county yield or revenue performance, regardless of how an individual farm performs."
    },
    {
      question: "'Basis risk' in area-based plans refers to:",
      options: [
        "The risk that premiums will increase",
        "The mismatch between individual farm and county performance",
        "The risk that the policy will be cancelled",
        "The cost of coverage above the base level"
      ],
      correct: 1,
      explanation: "Basis risk is the risk that your farm's performance differs from the county average. You may have a loss but receive no payment (if county does well), or receive payment without a loss."
    },
    {
      question: "Livestock Risk Protection (LRP) provides protection against:",
      options: [
        "Death of livestock",
        "Declining livestock market prices",
        "Disease outbreaks",
        "Increased feed costs"
      ],
      correct: 1,
      explanation: "LRP insures against declining livestock prices. It pays an indemnity when market prices fall below the selected coverage price."
    },
    {
      question: "The difference between LRP and LGM is:",
      options: [
        "LRP is for cattle, LGM is for swine",
        "LRP protects price only, LGM protects margin (price minus feed costs)",
        "LRP is federally subsidized, LGM is not",
        "LRP is area-based, LGM is individual"
      ],
      correct: 1,
      explanation: "LRP protects only the livestock price. LGM protects the gross margin, which is the livestock value minus feed costs - providing more comprehensive coverage."
    },
    {
      question: "Pasture, Rangeland, Forage (PRF) insurance is based on:",
      options: [
        "Actual forage production",
        "Livestock grazing patterns",
        "Rainfall indices in a grid area",
        "Hay prices"
      ],
      correct: 2,
      explanation: "PRF is a rainfall index insurance. It pays based on whether rainfall in a grid area falls below normal levels, not on actual forage production."
    },
    {
      question: "Whole-Farm Revenue Protection (WFRP) uses what to determine expected revenue?",
      options: [
        "APH production history",
        "Schedule F tax records",
        "County revenue data",
        "Commodity futures prices"
      ],
      correct: 1,
      explanation: "WFRP uses the farm's Schedule F tax records (typically 5 years) to establish expected revenue for the whole farm operation."
    },
    {
      question: "The Supplemental Coverage Option (SCO) covers losses in what range?",
      options: [
        "Below the individual coverage level",
        "From 86% down to the individual coverage level",
        "From 95% to 86%",
        "Above 100%"
      ],
      correct: 1,
      explanation: "SCO fills the gap from 86% of expected county revenue/yield down to the producer's individual policy coverage level (e.g., 86% to 75%)."
    },
    {
      question: "Enhanced Coverage Option (ECO) provides coverage for:",
      options: [
        "Deep losses below 50%",
        "Shallow losses from 86% to 90% or 95%",
        "Livestock only",
        "Prevented planting"
      ],
      correct: 1,
      explanation: "ECO covers 'shallow losses' - the range from 86% up to either 90% (ECO 90) or 95% (ECO 95) of expected county revenue."
    },
    {
      question: "STAX (Stacked Income Protection Plan) is available for:",
      options: [
        "All row crops",
        "Cotton only",
        "Livestock only",
        "Specialty crops"
      ],
      correct: 1,
      explanation: "STAX is specifically designed for upland cotton only. It provides area-based revenue protection that can be stacked on individual coverage."
    },
    {
      question: "A producer with individual RP at 75% who adds SCO would have SCO covering:",
      options: [
        "0% to 75%",
        "75% to 86%",
        "86% to 100%",
        "75% to 100%"
      ],
      correct: 1,
      explanation: "SCO covers the gap from 86% down to the individual coverage level. With 75% individual coverage, SCO covers the 75% to 86% range (11 percentage points)."
    },
    {
      question: "Margin Protection (MP) considers which factor that Revenue Protection does not?",
      options: [
        "Harvest price",
        "Production history",
        "Input costs",
        "County data"
      ],
      correct: 2,
      explanation: "MP protects the margin, which is revenue minus input costs (fertilizer, fuel, interest). Standard RP only considers revenue without subtracting costs."
    },
    {
      question: "Which plan would be best for a diversified farm growing specialty crops with no individual APH history?",
      options: [
        "Individual Yield Protection",
        "Revenue Protection",
        "Whole-Farm Revenue Protection (WFRP)",
        "Area Yield Protection"
      ],
      correct: 2,
      explanation: "WFRP is designed for diversified operations. It covers all commodities on the farm using tax records, making it ideal for specialty crops without APH history."
    },
    {
      question: "LGM-Dairy protects against:",
      options: [
        "Decline in milk prices only",
        "Decline in gross margin (milk price minus feed costs)",
        "Cattle death",
        "Herd reduction"
      ],
      correct: 1,
      explanation: "LGM-Dairy protects the gross margin between milk revenue and feed costs. It covers both sides of the margin - falling milk prices and rising feed costs."
    },
    {
      question: "PRF insurance requires producers to select:",
      options: [
        "Individual yield history",
        "2-month index intervals and grid location",
        "Livestock head count",
        "County revenue expectations"
      ],
      correct: 1,
      explanation: "PRF requires producers to select which 2-month intervals to insure (like April-May) and the grid(s) where their land is located."
    },
    {
      question: "The primary advantage of area-based plans over individual plans is:",
      options: [
        "Higher indemnity payments",
        "No production records required",
        "Lower premium subsidies",
        "Guaranteed payments every year"
      ],
      correct: 1,
      explanation: "Area-based plans don't require individual production records or APH. They use county data, making them accessible to producers without historical records."
    },
    {
      question: "When comparing RP to YP, which statement is TRUE?",
      options: [
        "YP has higher premiums than RP",
        "RP protects against price declines, YP does not",
        "YP is area-based, RP is individual",
        "Both use only the harvest price"
      ],
      correct: 1,
      explanation: "RP provides protection against price declines because it uses the higher of projected or harvest price. YP only uses the projected price, providing no price protection."
    },
    {
      question: "A producer wants coverage that pays based on county revenue from 90% down to 86%. They should purchase:",
      options: [
        "SCO",
        "ECO 90",
        "ARPI",
        "Individual RP"
      ],
      correct: 1,
      explanation: "ECO 90 covers county revenue from 90% to 86%. (ECO 95 would cover from 95% to 86%.) SCO covers from 86% down to individual coverage level."
    },
    {
      question: "For LRP, the coverage price is selected as:",
      options: [
        "A fixed dollar amount",
        "A percentage of the expected end price",
        "The current cash price",
        "The price at the beginning of the year"
      ],
      correct: 1,
      explanation: "LRP coverage price is selected as a percentage of the expected price (typically 70% to 100%). The producer chooses their level of price protection."
    },
    {
      question: "Which insurance plan would protect a cattle feeder against BOTH falling cattle prices and rising corn prices?",
      options: [
        "LRP (Livestock Risk Protection)",
        "LGM-Cattle (Livestock Gross Margin)",
        "PRF (Pasture, Rangeland, Forage)",
        "ARPI (Area Risk Protection Insurance)"
      ],
      correct: 1,
      explanation: "LGM-Cattle protects the gross margin (cattle value minus feed costs). It covers both falling cattle prices and rising feed costs like corn."
    },
    {
      question: "If a producer's individual corn yield is 180 bu/acre but the county average is 150 bu/acre, what is the concern with area-based coverage?",
      options: [
        "They will pay higher premiums",
        "They may not receive payment when their farm has average county performance",
        "Basis risk - their performance may differ significantly from county",
        "They are not eligible for coverage"
      ],
      correct: 2,
      explanation: "This producer performs above county average, creating basis risk. The county could have a 'loss' year (below 150) while this producer still yields well above their guarantee."
    },
    {
      question: "WFRP coverage levels can reach up to 85% for farms that:",
      options: [
        "Have the largest acreage",
        "Are highly diversified with multiple commodities",
        "Have been farming longest",
        "Only grow row crops"
      ],
      correct: 1,
      explanation: "WFRP rewards diversification. Farms growing multiple commodities can qualify for higher coverage levels as diversification reduces overall farm risk."
    },
    {
      question: "PRF basis risk exists because:",
      options: [
        "Premium payments vary year to year",
        "Actual rainfall at your location may differ from the grid average",
        "Forage quality varies by variety",
        "Livestock prices are volatile"
      ],
      correct: 1,
      explanation: "PRF uses grid-based rainfall data. Your specific location may receive different rainfall than the grid average, creating basis risk between your actual conditions and the index."
    },
    {
      question: "What coverage level range is typically available for Revenue Protection?",
      options: [
        "25% to 50%",
        "50% to 85%",
        "75% to 100%",
        "90% to 100%"
      ],
      correct: 1,
      explanation: "Revenue Protection coverage levels typically range from 50% to 85% in 5% increments. Above CAT (catastrophic) level, producers can select their desired level of protection."
    },
    {
      question: "The Harvest Price for Revenue Protection is determined:",
      options: [
        "At planting time",
        "At the sales closing date",
        "During the harvest price discovery period",
        "When the claim is filed"
      ],
      correct: 2,
      explanation: "The Harvest Price is determined during the harvest price discovery period, typically based on commodity futures prices during harvest months."
    },
    {
      question: "Which type of producer would benefit MOST from Yield Protection instead of Revenue Protection?",
      options: [
        "A producer who wants protection against price declines",
        "A producer who already has forward contracts locking in prices",
        "A producer with high price volatility concerns",
        "A beginning farmer with no marketing plan"
      ],
      correct: 1,
      explanation: "YP is ideal for producers who have already locked in prices through forward contracts or hedging. Since their price is secured, they only need protection against yield losses."
    },
    {
      question: "CAT coverage requires payment of:",
      options: [
        "A premium based on coverage level",
        "Only an administrative fee",
        "No payment at all",
        "Both premium and administrative fee"
      ],
      correct: 1,
      explanation: "CAT (Catastrophic) coverage requires only an administrative fee - there is no premium. The coverage provides 50% of approved yield at 55% of the price."
    },
    {
      question: "Area-based plans like ARPI are best suited for producers who:",
      options: [
        "Have yields consistently above county average",
        "Want individual farm protection",
        "Have yields that closely track county performance",
        "Need coverage for specialty crops"
      ],
      correct: 2,
      explanation: "Area-based plans work best when farm yields track county yields closely, minimizing basis risk. Producers with yields very different from county averages face greater basis risk."
    }
  ]
};

export default chapter4;
